import React, { useEffect, useState } from 'react'
import { Button, Card, Grid, makeStyles } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Formik, Form } from 'formik'
import { addVenue, fetchVenueById, resetVenue, updateVenueById } from 'store/venue'
import { Collection } from 'constants/index'
import { venueFormSchema } from 'schemas/venue'
import ProgressButton from 'components/ProgressButton'
import ViewHeader from 'components/ViewHeader'
import ActionBar from 'components/ActionBar'
import CustomTextField from 'components/Form/CustomTextField'
import AvatarPicker from 'components/AvatarPicker'
import { uploadImageFirstTime } from 'utils'

const VenueForm = ({ displayContext, onCloseModal, onSuccess }) => {
  const classes = useStyles()
  const { venueId } = useParams()
  const dispatch = useDispatch()
  const history = useHistory()
  const { user } = useSelector((state) => state.auth)
  const { venue } = useSelector((state) => state.venue)
  const [floorMapFile, setFloorMapFile] = useState(null)
  const [loading, setLoading] = useState(false)

  const initialValues = {
    floorNum: venue?.floorNum ?? '',
    roomNum: venue?.roomNum ?? '',
    title: venue?.title ?? '',
    imageName: venue?.imageName ?? null
  }

  const onCancel = () => {
    if (displayContext === 'page') {
      goBack()
    } else {
      onCloseModal()
    }
  }

  const goBack = () => {
    dispatch(resetVenue())
    history.goBack()
  }

  useEffect(() => {
    if (venueId) {
      dispatch(fetchVenueById(venueId))
    }
    return () => dispatch(resetVenue())
  }, [dispatch, venueId])

  const handleImageChange = (file) => {
    setFloorMapFile(file)
  }

  const handleUpload = async (name) => {
    await dispatch(updateVenueById(venueId, { imageName: name }))
  }

  const handleDelete = async () => {
    await dispatch(updateVenueById(venueId, { imageName: null }))
  }

  const onSubmit = async ({ title, floorNum, roomNum }) => {
    setLoading(true)
    const payload = {
      title: title.trim(),
      floorNum: floorNum.trim(),
      roomNum: roomNum.trim()
    }
    if (floorMapFile) {
      payload.imageName = await uploadImageFirstTime(
        floorMapFile,
        Collection.VENUES,
        user?.conferenceId
      )
    }

    let res, message
    if (venueId) {
      res = await dispatch(updateVenueById(venueId, payload))
      message = 'Venue updated successfully'
    } else {
      res = await dispatch(addVenue(payload))
      message = 'Venue created successfully'
    }
    setLoading(false)

    if (res && displayContext === 'page') {
      toast.success(message)
      goBack()
    }
    displayContext === 'modal' && onSuccess && onSuccess(res)
  }

  return (
    <Card
      elevation={displayContext === 'page' ? 3 : 0}
      className={displayContext === 'page' ? classes.card : classes.styledCard}>
      {displayContext === 'page' && (
        <ViewHeader title={venueId ? 'Edit venue' : 'Create venue'}>
          <ActionBar actionText="Back" onAction={goBack} />
        </ViewHeader>
      )}
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={onSubmit}
        validationSchema={venueFormSchema}>
        <Form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <CustomTextField label="Venue name" name="title" type="text" />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomTextField label="Floor" name="floorNum" type="text" />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomTextField label="Room" name="roomNum" type="text" />
            </Grid>
            <Grid item xs={12} lg={6}>
              <AvatarPicker
                title="Venue Floor Map"
                subTitle="Allowed Format: png/jpg ; Dimensions : 320x320"
                path={Collection.VENUES}
                handleCustomChange={handleImageChange}
                onUpload={handleUpload}
                onDelete={handleDelete}
                imageName={venueId ? venue?.imageName : null}
                conferenceId={user?.conferenceId}
                firsTimeUpload={!venueId}
              />
            </Grid>
          </Grid>
          <Grid container justifyContent="flex-end" spacing={3}>
            <Grid item>
              <Button size="large" disabled={loading} onClick={onCancel}>
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <ProgressButton type="submit" label="Save" inProgress={loading} disabled={loading} />
            </Grid>
          </Grid>
        </Form>
      </Formik>
    </Card>
  )
}
export default VenueForm

const useStyles = makeStyles({
  card: {
    padding: '24px',
    overflow: 'visible'
  },
  styledCard: {
    paddingTop: '1rem'
  }
})
